import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Edit,
  FileSignature,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  ShieldOff,
  User,
  Users,
} from "lucide-react";
import { useEmployee, useActivateEmployee, useDeactivateEmployee } from "../hooks";
import { resolveStatus } from "../types";
import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { EmployeeCardPreview } from "../components/EmployeeCardPreview";
import { EmployeeSignaturePanel } from "../components/EmployeeSignaturePanel";
import { EmployeeStatusBadge } from "../components/EmployeeStatusBadge";
import { EmployeeFamilyPanel } from "../components/EmployeeFamilyPanel";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";

// ─── helpers ────────────────────────────────────────────────────────────────

function v(val?: string | null) {
  return val?.trim() ? val.trim() : "—";
}

function fmtDate(val?: string | null) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString("en-KE", { day: "2-digit", month: "short", year: "numeric" });
}

function roleLabel(code: string) {
  return code.replace(/^ROLE_/, "").replaceAll("_", " ")
    .toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function Avatar({ name }: { name: string }) {
  const ini = name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");
  const colours = ["bg-emerald-500","bg-blue-500","bg-violet-500","bg-amber-500","bg-rose-500","bg-teal-500"];
  let n = 0; for (const c of name) n += c.charCodeAt(0);
  return (
    <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white ${colours[n % colours.length]}`}>
      {ini}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function EmployeeViewPage() {
  const { employeeId } = useParams();
  const { data: emp, isLoading, error } = useEmployee(Number(employeeId));
  const { hasRole, hasPermission } = useAuth();
  const confirm = useConfirm();
  const activateMutation = useActivateEmployee();
  const deactivateMutation = useDeactivateEmployee();

  if (isLoading) return <PageLoader label="Loading employee…" />;
  if (error || !emp) return <ErrorState message="Failed to load employee details." />;

  const allRoles = emp.roleCodes ?? emp.user?.roles ?? [];
  const displayRoles = allRoles.filter((r) => r !== "EMPLOYEE_SELF_SERVICE");

  const canManage = hasRole("SUPER_ADMIN") || hasPermission("HR_EMPLOYEE_MANAGE");
  const status = resolveStatus(emp);
  const isDisabled = status === "SUSPENDED" || status === "TERMINATED" || !emp.systemAccessEnabled;
  const togglingAccess = activateMutation.isPending || deactivateMutation.isPending;

  async function handleToggleActive() {
    if (isDisabled) {
      if (!(await confirm(`Reactivate ${emp.fullName}? This restores their system login access.`))) return;
      activateMutation.mutate(emp.id);
    } else {
      if (!(await confirm(`Deactivate ${emp.fullName}? This blocks their system login immediately.`))) return;
      deactivateMutation.mutate(emp.id);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/employees"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-4 w-4" /> Back to Employees
          </Link>
          <div className="mt-3 flex items-center gap-4">
            <Avatar name={emp.fullName} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{emp.fullName}</h1>
              <p className="text-sm text-slate-500">
                {emp.employeeNumber}{emp.jobTitle ? ` · ${emp.jobTitle}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <EmployeeStatusBadge
                  employmentStatus={emp.employmentStatus}
                  employmentType={emp.employmentType}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canManage && (
            <button
              type="button"
              disabled={togglingAccess}
              onClick={handleToggleActive}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${
                isDisabled
                  ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  : "border-red-300 text-red-700 hover:bg-red-50"
              }`}
            >
              {isDisabled ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
              {togglingAccess ? "Working..." : isDisabled ? "Activate" : "Deactivate"}
            </button>
          )}
          <Link to={`/employees/${emp.id}/edit`}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700">
            <Edit className="h-4 w-4" /> Edit
          </Link>
        </div>
      </div>

      {/* Quick stats bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Hire Date", value: fmtDate(emp.hireDate) },
          { label: "Department", value: emp.departmentName || "—" },
          { label: "Employment Type", value: emp.employmentType?.replaceAll("_", " ") || "—" },
          { label: "System Access", value: emp.systemAccessEnabled ? "Enabled" : "None" },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-2xl border bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            <p className="mt-1 font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_300px]">
        <div className="space-y-5">

          {/* Employment Details */}
          <Section title="Employment Details" icon={Briefcase}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Employee Number" value={v(emp.employeeNumber)} />
              <Field label="Job Title" value={v(emp.jobTitle)} />
              <Field label="Primary Department" value={v(emp.departmentName)} />
              <Field label="Hire Date" value={fmtDate(emp.hireDate)} />
              <Field label="Probation End" value={fmtDate(emp.probationEndDate)} />
              <Field label="Employment Status" value={emp.employmentStatus?.replaceAll("_", " ") || "—"} />
              <Field label="Employment Type" value={emp.employmentType?.replaceAll("_", " ") || "—"} />
              {emp.contractEndDate && (
                <Field label="Contract End Date" value={fmtDate(emp.contractEndDate)} />
              )}
              {emp.terminationDate && (
                <Field label="Termination Date" value={fmtDate(emp.terminationDate)} />
              )}
            </div>
            {(emp.departments ?? []).length > 1 && (
              <div className="mt-4 border-t pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">All Departments</p>
                <div className="flex flex-wrap gap-2">
                  {emp.departments!.map((d) => (
                    <span key={d.id} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {d.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* Personal Information */}
          <Section title="Personal Information" icon={User}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Date of Birth" value={fmtDate(emp.dateOfBirth)} />
              <Field label="Gender" value={v(emp.gender)} />
              <Field label="Marital Status" value={emp.maritalStatus?.replaceAll("_", " ") || "—"} />
              <Field label="National ID" value={v(emp.nationalIdNumber)} />
              <Field label="KRA PIN" value={v(emp.kraPin)} />
              <Field label="SHIF No." value={v(emp.nhifNumber)} />
              <Field label="NSSF No." value={v(emp.nssfNumber)} />
            </div>
          </Section>

          {/* Family & Dependents */}
          <Section title="Family & Dependents" icon={Users}>
            <EmployeeFamilyPanel employeeId={emp.id} />
          </Section>

          {/* E-signature -- usable across modules (Leave today, others later), settable by
              HR here or by the employee themselves from their own account settings. */}
          <Section title="E-Signature" icon={FileSignature}>
            <EmployeeSignaturePanel mode="admin" employeeId={emp.id} canEdit={canManage} />
          </Section>

          {/* Contact */}
          <Section title="Contact Details" icon={Phone}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Work Email" value={v(emp.workEmail)} />
              <Field label="Personal Email" value={v(emp.personalEmail)} />
              <Field label="Mobile Phone" value={v(emp.mobilePhone)} />
              <Field label="Alternative Phone" value={v(emp.alternativePhone)} />
            </div>
          </Section>

          {/* Address */}
          {(emp.addressLine1 || emp.cityTown || emp.country) ? (
            <Section title="Address" icon={MapPin}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Address Line 1" value={v(emp.addressLine1)} />
                <Field label="Address Line 2" value={v(emp.addressLine2)} />
                <Field label="City / Town" value={v(emp.cityTown)} />
                <Field label="Country" value={v(emp.country)} />
              </div>
            </Section>
          ) : null}

          {/* System Access */}
          <Section title="System Access & Roles" icon={ShieldCheck}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Login Access</p>
                <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-semibold ${emp.systemAccessEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {emp.systemAccessEnabled ? "Enabled" : "Not enabled"}
                </span>
              </div>
              {emp.user && (
                <Field label="Username" value={emp.user.username} />
              )}
            </div>

            {displayRoles.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Roles</p>
                <div className="flex flex-wrap gap-2">
                  {displayRoles.map((r) => (
                    <span key={r} className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                      {roleLabel(r)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(emp.user?.permissions?.length ?? 0) > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Permissions ({emp.user!.permissions!.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {emp.user!.permissions!.slice(0, 20).map((p) => (
                    <span key={p} className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p}</span>
                  ))}
                  {emp.user!.permissions!.length > 20 && (
                    <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      +{emp.user!.permissions!.length - 20} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </Section>

          {emp.businessCardEnabled && <EmployeeCardPreview employee={emp} />}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Quick Contact</p>
            <div className="space-y-2.5 text-sm">
              <a href={`mailto:${emp.workEmail || emp.personalEmail}`}
                className="flex items-center gap-2 text-slate-700 hover:text-emerald-700">
                <Mail className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="truncate">{v(emp.workEmail || emp.personalEmail)}</span>
              </a>
              <a href={`tel:${emp.mobilePhone}`}
                className="flex items-center gap-2 text-slate-700 hover:text-emerald-700">
                <Phone className="h-4 w-4 shrink-0 text-emerald-600" />
                <span>{v(emp.mobilePhone)}</span>
              </a>
              {emp.departmentName && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Building2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="truncate">{emp.departmentName}</span>
                </div>
              )}
            </div>
          </div>

        </aside>
      </div>
    </div>
  );
}
